import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import PercentageSeriesTooltip from "components/charts/modern/tooltip/PercentageSeriesTooltip";
import getContourColors from "components/stocks/modern/OperatingSegments/getContourColors";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import aeonik from "components/utils/aeonik";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";

interface SegmentsPieChartProps {
  className?: string;
  segments: {
    name: string;
    value: number;
  }[];
}
export default function SegmentsPieChart({ className, segments }: SegmentsPieChartProps) {
  const intl = useIntl();
  const breakpoints = useMinWidthTailwindBreakpoints();

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: "pie",
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 0,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: false,
      itemDistance: 16,
      layout: "horizontal",
      itemStyle: {
        color: colors.slate[800],
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "14px",
        textOverflow: "ellipsis",
        width: 240,
        textOutline: "none",
      },
    },
    series: [
      {
        type: "pie",
        size: "70%",
        innerSize: "50%",
        borderRadius: 4,
        showInLegend: true,
        data: segments.map((node, index) => {
          const theme = getContourColors(index);

          const color = theme.background;
          const borderColor = theme.text;
          const isOtherSegment = node.name === "Others";

          return {
            name: !isOtherSegment
              ? node.name
              : intl.formatMessage({
                  defaultMessage: "Outros",
                  id: "8K4Qok",
                  description: "Complemento do gráfico de receitas",
                }),
            y: node.value,
            color,
            borderColor,
            borderWidth: 1.5,
            custom: node,
            dataLabels: {
              enabled: breakpoints.lg || breakpoints.xl,
              style: {
                color: colors.slate[800],
                fontFamily: aeonik.style.fontFamily,
                fontWeight: "500",
                fontSize: "13px",
                textOutline: "none",
                textOverflow: "ellipsis",
                width: 240,
              },
            },
          };
        }),
      },
    ],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return renderToStaticMarkup(<PercentageSeriesTooltip context={this} />);
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        events: {
          legendItemClick: (event) => {
            event.preventDefault();

            return false;
          },
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            callback: () => {
              return breakpoints.lg || breakpoints.xl;
            },
          },
          chartOptions: {
            legend: {
              enabled: false,
            },
          },
        },
      ],
    },
  };
  return <HighchartsChart options={options} className={className} />;
}
